import { useState, useEffect } from "react";
import { FIXED_HOLIDAYS } from "../constants";
import { todayStrBR, maxDateStrBR } from "../lib/timezone";

// ─── COURT AVAILABILITY ───────────────────────────────────────────────────────

/** Convert "HH:MM" to total minutes */
function timeToMinutes(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

/**
 * Returns true if the given date string falls on a holiday
 * (fixed national holiday OR manually added by admin).
 */
function isHoliday(dateStr, holidays = []) {
  if (!dateStr) return false;
  const d = new Date(dateStr + "T12:00:00");
  const month = d.getMonth() + 1; // 1-based
  const day = d.getDate();

  // Check fixed national holidays (from constants.js)
  const fixedMatch = FIXED_HOLIDAYS.some(
    (h) => h.month === month && h.day === day
  );
  if (fixedMatch) return true;

  // Check manually added holidays (from Supabase)
  return holidays.some((h) => h.date === dateStr);
}

/**
 * Returns an array of [hour, maxCourts] slots for a given date string.
 * - Holiday  : treated as Sunday (same slots)
 * - Saturday : fixed slots
 * - Sunday   : fixed slots
 * - Weekdays : 06:00–14:00 every 30 min + fixed evening slots
 */
export function getCourtsForDay(dateStr, holidays = [], courtSlots = []) {
  if (!dateStr) return [];

  let dayType;
  if (isHoliday(dateStr, holidays)) {
    dayType = "holiday";
  } else {
    const dow = new Date(dateStr + "T12:00:00").getDay();
    if (dow === 6) dayType = "saturday";
    else if (dow === 0) dayType = "sunday";
    else dayType = "weekday";
  }

  // Usa slots do DB quando disponíveis
  if (courtSlots && courtSlots.length > 0) {
    return courtSlots
      .filter(s => s.dayType === dayType)
      .sort((a, b) => a.hour.localeCompare(b.hour))
      .map(s => [s.hour, s.maxCourts]);
  }

  // Fallback hardcoded
  if (dayType === "holiday" || dayType === "sunday") {
    return [["13:00", 2], ["15:00", 2], ["17:00", 2], ["19:00", 2]];
  }
  if (dayType === "saturday") {
    return [["13:00", 1], ["15:00", 2], ["17:00", 2], ["19:00", 2], ["21:00", 2]];
  }
  const slots = [];
  for (let m = 6 * 60; m <= 14 * 60; m += 30) {
    const h = String(Math.floor(m / 60)).padStart(2, "0");
    const min = String(m % 60).padStart(2, "0");
    slots.push([h + ":" + min, 1]);
  }
  slots.push(["21:00", 2]);
  return slots;
}

/** Returns true if slotA blocks slotB due to ±1h59 proximity rule */
function isBlockedByProximity(slotHour, bookedHour) {
  const diff = Math.abs(timeToMinutes(slotHour) - timeToMinutes(bookedHour));
  return diff > 0 && diff < 120; // strictly 1–119 min apart
}

/** True if a date+hour falls in a proximity-rule window (weekday morning / sunday afternoon) */
function usesProximityRule(dateStr, hour) {
  if (!dateStr) return false;
  const dow = new Date(dateStr + "T12:00:00").getDay();
  const mins = timeToMinutes(hour);
  if (dow === 0) return mins >= 14 * 60 && mins <= 20 * 60;
  if (dow >= 1 && dow <= 5) return mins >= 6 * 60 && mins <= 14 * 60;
  return false;
}

/** Count how many courts are already booked for a specific date + hour */
function countBookedCourts(challenges, dateStr, hour) {
  let count = 0;
  for (const c of challenges) {
    // Confirmed challenges
    if (c.confirmedDate) {
      const [d, t] = c.confirmedDate.split("T");
      if (d === dateStr && t && t.slice(0, 5) === hour) {
        count++;
        continue;
      }
    }
    // Proposed (unconfirmed) challenges
    if (c.status === "aguardando_confirmacao" && c.dates) {
      for (const dt of c.dates) {
        if (!dt) continue;
        const [d, t] = dt.split("T");
        if (d === dateStr && t && t.slice(0, 5) === hour) {
          count++;
          break;
        }
      }
    }
  }
  return count;
}

/** True if any already-booked or already-picked slot blocks this hour via the proximity rule */
function isBlockedByAnyBooked(challenges, excludeDates, dateStr, hour) {
  if (!usesProximityRule(dateStr, hour)) return false;

  // Check confirmed challenges
  for (const c of challenges) {
    if (!c.confirmedDate) continue;
    const [d, t] = c.confirmedDate.split("T");
    if (d !== dateStr) continue;
    const bookedHour = t ? t.slice(0, 5) : "";
    if (!bookedHour) continue;
    if (usesProximityRule(dateStr, bookedHour) && isBlockedByProximity(hour, bookedHour))
      return true;
  }

  // Check other dates already picked in the same proposal
  for (const ed of excludeDates) {
    if (!ed) continue;
    const [d, t] = ed.split("T");
    if (d !== dateStr) continue;
    const pickedHour = t ? t.slice(0, 5) : "";
    if (!pickedHour) continue;
    if (usesProximityRule(dateStr, pickedHour) && isBlockedByProximity(hour, pickedHour))
      return true;
  }

  return false;
}

/**
 * Returns available hour strings for a given date, taking into account
 * court capacity, the proximity rule, admin-blocked dates/hours, and holidays.
 */
export function getAvailableHours(dateStr, challenges, excludeDates = [], blockedDates = [], holidays = [], courtSlots = [], blockedPeriods = []) {
  if (!dateStr) return [];
  const slots = getCourtsForDay(dateStr, holidays, courtSlots);

  // Verifica blocked_periods (período)
  const inPeriod = blockedPeriods.find(p => dateStr >= p.dateFrom && dateStr <= p.dateTo);
  if (inPeriod && inPeriod.allDay) return [];

  // Find any admin block for this date (dia único)
  const adminBlock = blockedDates.find(b => b.date === dateStr);
  if (adminBlock && adminBlock.allDay) return [];

  const adminBlockedHours = [
    ...(adminBlock ? (adminBlock.blockedHours || []) : []),
    ...(inPeriod && !inPeriod.allDay ? (inPeriod.blockedHours || []) : []),
  ];

  return slots
    .filter(([hour, maxCourts]) => {
      // Admin-blocked specific hour
      const hourInt = parseInt(hour.split(":")[0], 10);
      if (adminBlockedHours.includes(hourInt)) return false;
      if (isBlockedByAnyBooked(challenges, excludeDates, dateStr, hour)) return false;
      const booked = countBookedCourts(challenges, dateStr, hour);
      const pickedCount = excludeDates.filter((d) => {
        if (!d) return false;
        const [ed, et] = d.split("T");
        return ed === dateStr && et && et.slice(0, 5) === hour;
      }).length;
      return booked + pickedCount < maxCourts;
    })
    .map(([hour]) => hour);
}

// ─── DATE/HOUR UTILS ──────────────────────────────────────────────────────────

/** Split a "YYYY-MM-DDTHH:MM..." string into { date, hour } */
export function toDateAndHour(dtStr) {
  if (!dtStr) return { date: "", hour: "" };
  const [date, time] = dtStr.split("T");
  const hour = time ? time.slice(0, 5) : "";
  return { date, hour };
}

/** Join date + hour into a Brazil-timezone ISO string for Supabase */
export function fromDateAndHour(date, hour) {
  if (!date || !hour) return "";
  return date + "T" + hour + ":00-03:00";
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────

/**
 * Date + hour picker that only shows courts available for the selected day.
 *
 * Props:
 *   value         – current ISO datetime string
 *   onChange      – called with new ISO string (or "" when incomplete)
 *   style         – style object applied to both inputs
 *   allChallenges – full challenges array for availability checks
 *   otherDates    – other already-picked dates in the same proposal
 *   blockedDates  – admin-blocked dates array from state
 *   holidays      – holidays array from state (manual + fixed resolved by isHoliday)
 */
export const RoundTimePicker = ({
  value,
  onChange,
  style,
  allChallenges = [],
  otherDates = [],
  blockedDates = [],
  holidays = [],
  courtSlots = [],
  blockedPeriods = [],
}) => {
  const parsed = toDateAndHour(value);
  const [localDate, setLocalDate] = useState(parsed.date);
  const [localHour, setLocalHour] = useState(parsed.hour);

  useEffect(() => {
    const p = toDateAndHour(value);
    setLocalDate(p.date);
    setLocalHour(p.hour);
  }, [value]);

  // Calculados em BRT via timezone.js para evitar bug de UTC e bypass mobile
  const today = todayStrBR();
  const maxDate = maxDateStrBR(15);

  // Validação de range — garante bloqueio mesmo em browsers mobile que ignoram min/max
  const dateOutOfRange = localDate && (localDate < today || localDate > maxDate);

  // Check if selected date is fully blocked by admin
  const adminBlock = blockedDates.find(b => b.date === localDate);
  const dayFullyBlocked = adminBlock && adminBlock.allDay;

  const availableHours = dateOutOfRange
    ? []
    : getAvailableHours(localDate, allChallenges, otherDates, blockedDates, holidays, courtSlots, blockedPeriods);

  function handleDate(d) {
    const isValid = d >= today && d <= maxDate;
    setLocalDate(d);
    setLocalHour("");
    onChange("");
    // Se o browser mobile deixou passar uma data fora do range, limpa silenciosamente
    if (!isValid) setLocalDate("");
  }

  function handleHour(h) {
    setLocalHour(h);
    if (localDate && h) onChange(fromDateAndHour(localDate, h));
    else onChange("");
  }

  return (
    <div style={{ display: "flex", gap: 6, flex: 1 }}>
      <input
        type="date"
        min={today}
        max={maxDate}
        value={localDate}
        onChange={(e) => handleDate(e.target.value)}
        style={{ ...style, flex: 2, marginBottom: 0, minHeight: 44, colorScheme: "light" }}
      />
      <select
        value={localHour}
        onChange={(e) => handleHour(e.target.value)}
        disabled={!localDate || dayFullyBlocked || dateOutOfRange}
        style={{ ...style, flex: 1, marginBottom: 0, minHeight: 44, opacity: localDate ? 1 : 0.5 }}
      >
        <option value="">
          {!localDate
            ? "Hora"
            : dateOutOfRange
            ? "Data inválida"
            : dayFullyBlocked
            ? "Bloqueado"
            : availableHours.length === 0
            ? "Sem vagas"
            : "Hora"}
        </option>
        {availableHours.map((h) => (
          <option key={h} value={h}>
            {h}
          </option>
        ))}
      </select>
    </div>
  );
};
